// GearVault API client — drop-in replacement for supabase.js
// Mirrors the supabase-js interface used throughout GearVault.jsx

const API_URL   = 'https://api.gearvault.xyz';
const TOKEN_KEY = 'gearvault-auth-token';
const USER_KEY  = 'gearvault-auth-user';

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function setSession(token, user) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {}
}

function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {}
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) return { data: null, error: { message: data.error || 'Request failed' } };
  return { data, error: null };
}

// ── Auth ──────────────────────────────────────────────────────────────────────
const auth = {
  async signInWithPassword({ email, password }) {
    const { data, error } = await apiFetch('/auth/login', {
      method: 'POST', body: { email, password },
    });
    if (error) return { data: null, error };
    setSession(data.token, data.user);
    // Store session cache and reload so App.jsx picks up the new session
    localStorage.setItem('gearvault-session-user', JSON.stringify(data.user));
    window.location.reload();
    return { data: { user: data.user, session: { access_token: data.token } }, error: null };
  },

  async signUp({ email, password }) {
    const { data, error } = await apiFetch('/auth/signup', {
      method: 'POST', body: { email, password },
    });
    if (error) return { data: null, error };
    setSession(data.token, data.user);
    return { data: { user: data.user }, error: null };
  },

  async signOut() {
    clearSession();
    return { error: null };
  },

  async updateUser({ password }) {
    return await apiFetch('/auth/change-password', {
      method: 'POST', body: { password },
    });
  },

  async getSession() {
    const token = getToken();
    if (!token) return { data: { session: null }, error: null };
    try {
      const user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
      return { data: { session: { access_token: token, user } }, error: null };
    } catch {
      return { data: { session: null }, error: null };
    }
  },

  onAuthStateChange(callback) {
    this.getSession().then(({ data }) => {
      callback(data.session ? 'SIGNED_IN' : 'SIGNED_OUT', data.session);
    });
    return { data: { subscription: { unsubscribe: () => {} } } };
  },

  async resetPasswordForEmail() {
    return { error: { message: 'Password reset email not configured. Use Account > Change Password instead.' } };
  },
};

// ── Query builder — mirrors supabase.from() chaining ─────────────────────────
function buildQuery(table) {
  const q = {
    _table:    table,
    _method:   'GET',
    _body:     null,
    _id:       null,
    _isSingle: false,

    select()        { return this; },
    eq(col, val)    { if (col === 'id') this._id = val; return this; },
    order()         { return this; },
    single()        { this._isSingle = true; return this; },

    insert(body) {
      this._method = 'POST';
      this._body   = Array.isArray(body) ? body[0] : body;
      return this;
    },

    update(body) {
      this._method = 'PUT';
      this._body   = body;
      return this;
    },

    delete() {
      this._method = 'DELETE';
      return this;
    },

    async then(resolve) {
      try {
        let path = `/api/${this._table}`;
        if (this._id && (this._method === 'PUT' || this._method === 'DELETE' || this._isSingle)) {
          path += `/${this._id}`;
        }
        const { data, error } = await apiFetch(path, {
          method: this._method,
          body:   this._method !== 'GET' && this._method !== 'DELETE' ? this._body : undefined,
        });
        if (error) return resolve({ data: null, error });
        if (this._isSingle && Array.isArray(data)) {
          return resolve({ data: data[0] ?? null, error: null });
        }
        return resolve({ data, error: null });
      } catch (e) {
        resolve({ data: null, error: { message: e.message } });
      }
    },
  };
  return q;
}

export const supabase = {
  auth,
  from: (table) => buildQuery(table),
};
